'use client';

import type { QuantityDiscount } from '@eliadmin/core/src/api/products/types/product-types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { QuantityDiscountTierQuantityLabel } from '@eliadmin/core/src/components/product/detail/quantity-discount-tier-quantity-label';
import { QuantityDiscountTierMessage } from '@eliadmin/core/src/components/product/detail/quantity-discount-tier-message';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';
import { isSellable } from '@eliadmin/core/src/utils/product-utils';
import { formatPrice } from '@eliadmin/core/src/utils/price-utils';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useMemo } from 'react';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { getProductPriceDisplay } from '@eliadmin/core/src/utils/product-price-utils';

const getDiscountedPrice = (priceWithVat: number, discount: QuantityDiscount) => {
  if (discount.discount_type === 'fixed') {
    return Math.max(0, priceWithVat - discount.discount);
  }
  return Math.max(0, priceWithVat - (priceWithVat * discount.discount) / 100);
};

export const ProductQuantityDiscounts = () => {
  const product = useVariantsStore(s => s.product);
  const selectedVariant = useVariantsStore(s => s.selectedVariant);
  const selectedQuantity = useVariantsStore(s => s.selectedQuantity);
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const setSelectedQuantity = useVariantsStore(s => s.setSelectedQuantity);
  const isWholesaler = useCustomerStore(s => s.me?.customer.is_wholesaler);

  const currency = useGlobalStore(state => state.generalLayout.currency);
  const currencySettings = useGlobalStore(state => state.generalLayout.currency_settings);
  const priceWithoutVatFirst = useGlobalStore(
    state => state.templateSettings.product_detail?.price_without_vat_first?.enabled,
  );

  const quantityDiscounts = useMemo(() => {
    const raw = (product as { quantity_discounts?: QuantityDiscount[] | QuantityDiscount })
      .quantity_discounts;
    if (!raw) return [];
    const discounts = Array.isArray(raw) ? raw : [raw];

    return discounts
      .filter(discount => Number(discount.min_quantity) > 0)
      .sort((a, b) => Number(a.min_quantity) - Number(b.min_quantity));
  }, [product]);

  const desiredObject = selectedVariant ?? product;
  const priceDisplay = getProductPriceDisplay(desiredObject, {
    isWholesaler,
    priceWithoutVatFirst,
  });
  const basePriceWithVat = priceDisplay.current ?? 0;

  const nextDiscount = useMemo(
    () => quantityDiscounts.find(discount => Number(discount.min_quantity) > selectedQuantity),
    [quantityDiscounts, selectedQuantity],
  );
  const activeDiscount = useMemo(() => {
    const inRange = quantityDiscounts.filter(discount => {
      const min = Number(discount.min_quantity ?? 0);
      const max =
        discount.max_quantity == null ? Number.POSITIVE_INFINITY : Number(discount.max_quantity);
      return selectedQuantity >= min && selectedQuantity <= max;
    });

    if (inRange.length) {
      return inRange.sort((a, b) => Number(b.min_quantity) - Number(a.min_quantity))[0];
    }

    return quantityDiscounts
      .filter(discount => Number(discount.min_quantity) <= selectedQuantity)
      .sort((a, b) => Number(b.min_quantity) - Number(a.min_quantity))[0];
  }, [quantityDiscounts, selectedQuantity]);
  const tierToDisplayInHeader = nextDiscount ?? activeDiscount ?? quantityDiscounts.at(-1);

  if (
    priceDisplay.priceOnRequest ||
    (!selectedVariant && (product.variants?.length ?? 0) > 0) ||
    !quantityDiscounts.length
  ) {
    return null;
  }

  return (
    <Accordion
      type="single"
      collapsible
      className={cn(
        'w-full rounded-[18px] border border-[#E2E4E7] bg-white',
        'ea-product-quantity-discounts',
      )}
    >
      <AccordionItem
        value="quantity-discounts"
        className={cn('border-none', 'ea-product-quantity-discounts__item')}
      >
        <AccordionTrigger
          className={cn(
            'rounded-[18px] px-4 py-3 text-left no-underline hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]',
            'ea-product-quantity-discounts__trigger',
          )}
        >
          <div
            className={cn(
              'flex min-w-0 flex-col items-start gap-0.5 pr-2',
              'ea-product-quantity-discounts__header',
            )}
          >
            <span
              className={cn(
                'dlz-body-s-medium text-[#0F1012]',
                'ea-product-quantity-discounts__title',
              )}
            >
              {t('text3480')}
            </span>
            {tierToDisplayInHeader && (
              <span
                className={cn(
                  'dlz-caption font-normal text-[#6B7079]',
                  'ea-product-quantity-discounts__tier-message',
                )}
              >
                <QuantityDiscountTierMessage
                  locale={locale}
                  discountLabel={
                    tierToDisplayInHeader.discount_type === 'fixed'
                      ? `-${formatPrice(
                          tierToDisplayInHeader.discount,
                          currency,
                          currencySettings,
                        )}`
                      : `-${tierToDisplayInHeader.discount}%`
                  }
                  priceAfterDiscountLabel={formatPrice(
                    getDiscountedPrice(basePriceWithVat, tierToDisplayInHeader),
                    currency,
                    currencySettings,
                  )}
                  minQuantity={Number(tierToDisplayInHeader.min_quantity)}
                  isLastTier={!nextDiscount}
                />
              </span>
            )}
          </div>
        </AccordionTrigger>
        <AccordionContent
          className={cn('flex flex-col gap-2 px-4 pt-0', 'ea-product-quantity-discounts__content')}
        >
          {quantityDiscounts.map((discount, index) => {
            const minQuantity = Number(discount.min_quantity);
            const discountedPriceWithVat = getDiscountedPrice(basePriceWithVat, discount);
            const isActiveTier = activeDiscount?.id === discount.id;
            const canSelectTier = isSellable(desiredObject, minQuantity);
            const isLastTier = index === quantityDiscounts.length - 1;

            return (
              <button
                key={discount.id}
                type="button"
                aria-pressed={isActiveTier}
                className={cn(
                  'flex w-full items-center justify-between gap-3 rounded-[12px] border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1012]',
                  isActiveTier
                    ? 'border-[#0F1012] bg-[#F5F6F7]'
                    : 'border-[#E2E4E7] bg-white hover:border-[#9096A0]',
                  !canSelectTier && 'cursor-not-allowed opacity-50 hover:border-[#E2E4E7]',
                  'ea-product-quantity-discounts__tier',
                  isActiveTier && 'ea-product-quantity-discounts__tier--active',
                  !canSelectTier && 'ea-product-quantity-discounts__tier--disabled',
                )}
                disabled={!canSelectTier}
                onClick={() => {
                  if (!canSelectTier) return;
                  setSelectedQuantity(minQuantity);
                }}
              >
                <span
                  className={cn(
                    'dlz-body-s-medium text-[#0F1012]',
                    'ea-product-quantity-discounts__tier-quantity',
                  )}
                >
                  <QuantityDiscountTierQuantityLabel
                    locale={locale}
                    minQuantity={minQuantity}
                    isLastTier={isLastTier}
                  />
                </span>
                <span
                  className={cn(
                    'flex items-center gap-3',
                    'ea-product-quantity-discounts__tier-right',
                  )}
                >
                  <span
                    className={cn(
                      'dlz-body-s-medium text-[#D7191F]',
                      'ea-product-quantity-discounts__tier-discount',
                    )}
                  >
                    {discount.discount_type === 'fixed'
                      ? `-${formatPrice(discount.discount, currency, currencySettings)}`
                      : `-${discount.discount.toFixed(0)}%`}
                  </span>
                  <span
                    className={cn(
                      'dlz-body-s-medium min-w-[64px] text-right text-[#0F1012]',
                      'ea-product-quantity-discounts__tier-price',
                    )}
                  >
                    {formatPrice(discountedPriceWithVat, currency, currencySettings)}
                  </span>
                </span>
              </button>
            );
          })}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
